import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { CreditCard, CheckCircle, Warning, XCircle, Clock } from '@phosphor-icons/react';
import PaymentModal from './PaymentModal';

const SubscriptionStatus = ({ showDetails = false }) => {
  const navigate = useNavigate();
  const { 
    subscription, 
    customer, 
    getSubscriptionStatus, 
    isInGracePeriod, 
    getGracePeriodDaysRemaining,
    startSubscription,
    openBillingPortal 
  } = useSubscription();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const status = getSubscriptionStatus();

  const handlePaymentSuccess = async (paymentMethod) => {
    setIsProcessingPayment(true);
    try {
      // For new subscriptions, use the existing startSubscription function
      await startSubscription(paymentMethod);
      setShowPaymentModal(false);
      // The subscription context will refresh automatically
    } catch (error) {
      console.error('Error processing payment:', error);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentError = (errorMessage) => {
    console.error('Payment error:', errorMessage);
    setIsProcessingPayment(false);
  };

  const handleStartSubscription = () => {
    setShowPaymentModal(true);
  };

  const getStatusIcon = () => {
    switch (status.status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'past_due':
        return <Warning className="w-5 h-5 text-yellow-500" />;
      case 'suspended':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'inactive':
        return <CreditCard className="w-5 h-5 text-gray-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status.status) {
      case 'active':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'past_due':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'suspended':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'inactive':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  if (!showDetails) {
    // Compact status display
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor()}`}>
        {getStatusIcon()}
        <span>{status.message}</span>
      </div>
    );
  }

  // Detailed status display
  return (
    <div className="space-y-4">
      {/* Status Header */}
      <div className={`flex items-center gap-3 p-4 rounded-lg border ${getStatusColor()}`}>
        {getStatusIcon()}
        <div>
          <h3 className="font-semibold">{status.message}</h3>
          {isInGracePeriod() && (
            <p className="text-sm opacity-75">
              {getGracePeriodDaysRemaining()} days remaining to update payment method
            </p>
          )}
        </div>
      </div>

      {/* Subscription Details */}
      {subscription && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Subscription Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Plan:</span>
              <span className="ml-2 font-medium">Shift Healthcare Pro</span>
            </div>
            <div>
              <span className="text-gray-500">Price:</span>
              <span className="ml-2 font-medium">$80/month</span>
            </div>
            <div>
              <span className="text-gray-500">Status:</span>
              <span className="ml-2 font-medium capitalize">{subscription.status}</span>
            </div>
            <div>
              <span className="text-gray-500">Next Billing:</span>
              <span className="ml-2 font-medium">{formatDate(subscription.current_period_end)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details */}
      {customer && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Billing Information</h4>
          <div className="text-sm">
            <div className="mb-2">
              <span className="text-gray-500">Customer ID:</span>
              <span className="ml-2 font-mono text-xs">{customer.id}</span>
            </div>
            <div>
              <span className="text-gray-500">Email:</span>
              <span className="ml-2">{customer.email}</span>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {!subscription && (
          <button
            onClick={handleStartSubscription}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Start Subscription - $80/month
          </button>
        )}
        
        {customer && (
          <button
            onClick={() => {
              try {
                openBillingPortal();
              } catch (error) {
                // Fallback to Account Settings if billing portal fails
                console.log('Billing portal not available, navigating to Account Settings');
                navigate('/dashboard/settings');
              }
            }}
            className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Manage Billing
          </button>
        )}
      </div>

      {/* Grace Period Warning */}
      {isInGracePeriod() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Warning className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-yellow-800">Payment Failed</h4>
              <p className="text-sm text-yellow-700 mt-1">
                Your payment method failed. You have {getGracePeriodDaysRemaining()} days to update your payment method 
                before your account is suspended. Please update your payment method to continue using the service.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Suspended Account Notice */}
      {status.status === 'suspended' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-800">Account Suspended</h4>
              <p className="text-sm text-red-700 mt-1">
                Your account has been suspended due to failed payment. Please update your payment method 
                and pay any outstanding invoices to reactivate your account.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        loading={isProcessingPayment}
        disabled={isProcessingPayment}
        title="Start Subscription"
        description="Complete your payment to activate your Shift Healthcare Pro subscription."
      />
    </div>
  );
};

export default SubscriptionStatus;